import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

import { Colors } from "@/constants/colors";
import { COMPONENTS } from "@/constants/components";
import { ExperimentVariants } from "@/constants/experiments";
import { useGender } from "@/utils/GenderProvider";
import {
    getTestMechanismVariant,
    setTestMechanismVariantConversionEvent,
    setTestMechanismVariantSeen,
} from "@/utils/services/api";

import { useFlowManager } from "../FlowProvider";

import {
    backgroundImgs,
    femaleDefaultCombination,
    maleDefaultCombination,
    subtitles,
    titles,
    varinatsStyles,
} from "./variants";

type SelectedVariants = {
    backgroundImg: string;
    title: string;
    subtitle: string;
    styles: {
        logoColor: Colors;
        textColor: string;
        contentStyle: string;
    };
};
type LoaderScreenVariantsContextType = SelectedVariants & {
    handleLoaderScreenConversionEvent: () => void;
    isLoaderScreenTest: boolean;
    isLoading: boolean;
};

const LoaderScreenVariantsContext = createContext<LoaderScreenVariantsContextType>({
    ...femaleDefaultCombination,
    handleLoaderScreenConversionEvent: () => null,
    styles: varinatsStyles.femaleDefault,
    isLoaderScreenTest: false,
    isLoading: true,
});
type SelectedKeys = [keyof typeof backgroundImgs, keyof typeof titles, keyof typeof subtitles];

const LoaderScreenVariantsProvider = ({ children }: PropsWithChildren) => {
    const [isLoading, setIsLoading] = useState(true);
    const { isFemale } = useGender();
    const { component } = useFlowManager({});
    const [selectedKeys, setSelectedKeys] = useState<SelectedKeys>();
    const [selectedVariants, setSelectedVariants] = useState<SelectedVariants>(
        isFemale ? femaleDefaultCombination : maleDefaultCombination
    );
    const [isConversionEventSent, setIsConversionEventSent] = useState<boolean>(false);
    const isFirstLoaderScreen = component === COMPONENTS.FIRST_LOADER;

    const isLoaderScreenTest = false;
    const variant = ExperimentVariants.CONTROL;
    const isVariantGet = variant !== undefined;

    /*useEffect(() => {
        isFirstLoaderScreen && enableLoaderScreenTest();
    }, [isFirstLoaderScreen]);*/

    const passSelectedVariants = (variants: SelectedKeys) => {
        variants.forEach((variant) => setTestMechanismVariantSeen(variant));
    };

    type PromisesArr = [
        Promise<keyof typeof backgroundImgs>,
        Promise<keyof typeof titles>,
        Promise<keyof typeof subtitles>,
    ];
    const getVariants = useCallback(async () => {
        Promise.all([
            getTestMechanismVariant(Object.keys(backgroundImgs)),
            getTestMechanismVariant(Object.keys(titles)),
            getTestMechanismVariant(Object.keys(subtitles)),
        ] as PromisesArr)
            .then((values) => {
                setSelectedKeys(values);
                setIsLoading(false);
                passSelectedVariants(values);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        if (selectedKeys) {
            setSelectedVariants({
                backgroundImg: backgroundImgs[selectedKeys[0]],
                title: titles[selectedKeys[1]],
                subtitle: subtitles[selectedKeys[2]],
                styles: varinatsStyles[selectedKeys[0]],
            });
        }
    }, [selectedKeys]);

    const handleLoaderScreenConversionEvent = useCallback(() => {
        if (isLoaderScreenTest && selectedKeys && !isConversionEventSent) {
            selectedKeys?.forEach((variant) => setTestMechanismVariantConversionEvent(variant));
            setIsConversionEventSent(true);
        }
    }, [isLoaderScreenTest, selectedKeys, isConversionEventSent]);

    useEffect(() => {
        if (isVariantGet) {
            if (isLoaderScreenTest && isFirstLoaderScreen) {
                getVariants();
            } else {
                setIsLoading(false);
            }
        }
    }, [isVariantGet, isLoaderScreenTest, isFirstLoaderScreen]);

    return (
        <LoaderScreenVariantsContext.Provider
            value={{
                ...selectedVariants,
                isLoaderScreenTest,
                isLoading,
                handleLoaderScreenConversionEvent,
            }}
        >
            {children}
        </LoaderScreenVariantsContext.Provider>
    );
};

export const useLoaderScreenVariants = () => {
    return useContext(LoaderScreenVariantsContext);
};

export default LoaderScreenVariantsProvider;
